import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppLifecycle } from "@/hooks/use-app-lifecycle";
import { useBundles } from "@/hooks/use-bundles";
import { useContinueReading } from '@/hooks/use-continue-reading';
import { NetflixHero } from '@/components/my-books/NetflixHero';
import { NetflixThumbnailRow } from '@/components/my-books/NetflixThumbnailRow';
import { BookDialog } from '@/components/BookDialog';
import { navigateToBook } from '@/utils/reading-navigation';
import { toast } from '@/hooks/use-toast';
import { useEnhancedReadingList } from '@/hooks/use-enhanced-reading-list';
import { useBooks } from '@/hooks/use-books';
import { motion, AnimatePresence } from 'framer-motion';
import { setActiveTransitionBookId } from '@/components/BookCard';
import { Book } from '@/types/book';

export default function MyBundles() {
  const [selectedBook, setSelectedBook] = useState<any>(null);
  
  const navigate = useNavigate();

  const { isAppVisible } = useAppLifecycle();
  const {
    queryClient,
    isLoading: isBundlesLoading
  } = useBundles();
  const {
    allBooks,
    isLoading: isBooksLoading
  } = useBooks();
  // Use lightweight continue reading hook - instant from cache
  const { continueReadingBooks: continueReadingData } = useContinueReading(allBooks, 15);
  const { readingList } = useEnhancedReadingList();

  // View Transitions API - native-feeling book card → dialog morph
  const handleBookSelect = useCallback((book: Book | null) => {
    if (book) {
      setActiveTransitionBookId(Number(book.id));
      
      if (document.startViewTransition) {
        document.startViewTransition(() => {
          setSelectedBook(book);
        });
      } else {
        setSelectedBook(book);
      }
      
      setTimeout(() => setActiveTransitionBookId(null), 400);
    } else {
      if (document.startViewTransition) {
        document.startViewTransition(() => {
          setSelectedBook(null);
        });
      } else {
        setSelectedBook(null);
      }
      setTimeout(() => setActiveTransitionBookId(null), 400);
    }
  }, []);

  // Purchase handling
  const handlePurchase = async () => {
    toast({
      title: "Success!",
      description: "Bundle purchased successfully."
    });
  };

  // Access checking logic
  const checkBookAccess = (book: any) => {
    if (!book) return false;
    return true;
  };

  // Cache checks for loading state - don't block on progress loading
  const booksCachedEntries = queryClient.getQueriesData({
    queryKey: ['books-list']
  }) as any[];
  const hasBooksCache = Array.isArray(booksCachedEntries) && booksCachedEntries.some(([, data]) => Array.isArray(data) && data.length > 0);
  const activeBundleCached = queryClient.getQueryData(['activeBundle']);
  const permanentBundlesCached = queryClient.getQueryData(['permanentBundles']) as any[] | undefined;
  const upcomingBundleCached = queryClient.getQueryData(['upcomingBundle']);
  const hasBundlesCache = !!(activeBundleCached || Array.isArray(permanentBundlesCached) && permanentBundlesCached.length > 0 || upcomingBundleCached);
  const hasCoreCache = hasBooksCache && hasBundlesCache;
  // Only block on books and bundles loading, not on continue reading or reading list
  const isLoading = !hasCoreCache && (isBooksLoading || isBundlesLoading);

  // Prepare book data for rows - use pre-formatted data from useContinueReading
  const continueReadingBooks = useMemo(() => 
    continueReadingData.slice(0, 15).map(book => ({
      id: book.id,
      title: book.title,
      cover_image_url: book.cover_image_url,
      lastPage: book.lastPage
    })),
    [continueReadingData]
  );

  const myListBooks = useMemo(() => 
    readingList
      .map(item => {
        const book = allBooks.find(b => b.id === item.book_id);
        if (!book) return null;
        // Find progress from continue reading data
        const bookProgress = continueReadingData.find(bp => bp.id === book.id);
        return {
          id: book.id,
          title: book.title,
          cover_image_url: book.cover_image_url,
          lastPage: bookProgress?.lastPage || 0
        };
      })
      .filter(Boolean) as { id: number; title: string; cover_image_url?: string | null; lastPage: number }[],
    [readingList, allBooks, continueReadingData]
  );

  // Coming Soon books - from upcoming bundle
  const comingSoonBooks = useMemo(() => 
    allBooks
      .filter(book => book.bundle?.status === 'upcoming')
      .slice(0, 15)
      .map(book => ({
        id: book.id,
        title: book.title,
        cover_image_url: book.cover_image_url,
        lastPage: 0
      })),
    [allBooks]
  );

  // New Releases - from active bundle
  const newReleaseBooks = useMemo(() => 
    allBooks
      .filter(book => book.bundle?.status === 'active')
      .slice(0, 15)
      .map(book => ({
        id: book.id,
        title: book.title,
        cover_image_url: book.cover_image_url,
        lastPage: 0
      })),
    [allBooks]
  );

  // Get current hero book cover for glow effect
  const heroBookCover = continueReadingData[0]?.cover_image_url || allBooks[0]?.cover_image_url;

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-8 relative" style={{
      paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 6rem)'
    }}>
      {/* Ambient glow - bleeds behind content like FreeBooksSection */}
      <AnimatePresence mode="wait">
        {heroBookCover && (
          <motion.div
            key={heroBookCover}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, delay: 3 }}
            className="absolute left-0 right-0 top-[120px] h-[80vh] z-0 pointer-events-none"
            style={{
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 30%, black 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 30%, black 100%)'
            }}
          >
            <div
              className="w-full h-full blur-[100px] opacity-50 saturate-[1.3]"
              style={{
                backgroundImage: `url(${heroBookCover})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                transform: 'scale(1.2)',
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Netflix-style Hero */}
      <div className="relative z-10">
        <NetflixHero 
          onBookClick={(book) => handleBookSelect(book)}
        />
      </div>

      {/* Content Rows */}
      <div className="relative z-10 pb-40 space-y-3 mt-4">
        {isLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={isAppVisible ? { opacity: 1 } : false}
            className="text-center py-20"
          >
            <div className="w-12 h-12 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center mx-auto mb-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white/60"></div>
            </div>
            <p className="text-white/60 font-apple text-sm">Loading your library...</p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isAppVisible ? { opacity: 1, y: 0 } : false}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-3"
          >
            {/* Continue Reading Row */}
            {continueReadingBooks.length > 0 && (
              <NetflixThumbnailRow
                title="Continue Reading"
                books={continueReadingBooks}
                onBookClick={(book) => {
                  const fullBook = allBooks.find(b => b.id === book.id);
                  if (fullBook) handleBookSelect({ ...fullBook, lastPage: book.lastPage });
                }}
              />
            )}

            {/* My List Row - above new releases */}
            {myListBooks.length > 0 && (
              <NetflixThumbnailRow
                title="My List"
                books={myListBooks}
                onBookClick={(book) => {
                  const fullBook = allBooks.find(b => b.id === book.id);
                  if (fullBook) handleBookSelect({ ...fullBook, lastPage: book.lastPage });
                }}
              />
            )}

            {/* New Releases Row */}
            {newReleaseBooks.length > 0 && (
              <NetflixThumbnailRow
                title="New Releases"
                books={newReleaseBooks}
                onBookClick={(book) => {
                  const fullBook = allBooks.find(b => b.id === book.id);
                  if (fullBook) handleBookSelect(fullBook);
                }}
              />
            )}

            {/* Coming Soon Row */}
            {comingSoonBooks.length > 0 && (
              <NetflixThumbnailRow
                title="Coming Soon"
                books={comingSoonBooks}
                onBookClick={(book) => {
                  const fullBook = allBooks.find(b => b.id === book.id);
                  if (fullBook) handleBookSelect(fullBook);
                }}
              />
            )}
          </motion.div>
        )}
      </div>

      {/* Book Dialog */}
      {selectedBook && (
        <BookDialog
          book={selectedBook}
          isOpen={!!selectedBook}
          onClose={() => handleBookSelect(null)}
          hasAccess={checkBookAccess(selectedBook)}
          onReadNow={() => {
            if (selectedBook) {
              navigateToBook(selectedBook.id, navigate, {
                currentPage: (selectedBook as any).lastPage
              });
            }
          }}
          onPurchase={handlePurchase}
          onBookClick={book => handleBookSelect(book)}
          allBooks={allBooks}
        />
      )}
    </div>
  );
}
